import { useState, useEffect, useRef } from 'react';
import PaymentSummaryModal from '../../../components/PaymentSummaryModal';

interface OutletCheckoutModalProps {
  onClose: () => void;
  onConfirm: (countedAmount: number, acceptDifference: boolean, difference: number) => Promise<void>;
  sessionId?: number;
  outletName: string;
  outletOpeningCash: number;
  remainingCash: number;
  totalCounterOpeningCash: number;
  sales: number;
  cashIn: number;
  cashOut: number;
  cashAmount: number;
  bankMobile: number;
  salesReturn: number;
  refund: number;
  creditApplied: number;
  reservedCounterCash: number;
  accountableCash: number;
  closingCash: number;
  isLoading?: boolean;
}

const BILL_DENOMINATIONS = [
  { key: 'thousand', value: 1000 },
  { key: 'fiveHundred', value: 500 },
  { key: 'twoHundred', value: 200 },
  { key: 'hundred', value: 100 },
  { key: 'fifty', value: 50 },
  { key: 'twenty', value: 20 },
  { key: 'ten', value: 10 },
  { key: 'five', value: 5 },
  { key: 'two', value: 2 },
  { key: 'one', value: 1 },
] as const;

type BillKey = typeof BILL_DENOMINATIONS[number]['key'];
type BillCounts = Record<BillKey, number>;

const EMPTY_BILL_COUNTS: BillCounts = {
  thousand: 0, fiveHundred: 0, twoHundred: 0, hundred: 0, fifty: 0, twenty: 0, ten: 0, five: 0, two: 0, one: 0,
};

export default function OutletCheckoutModal({
  onClose,
  onConfirm,
  sessionId,
  outletName,
  outletOpeningCash,
  remainingCash,
  totalCounterOpeningCash,
  sales,
  cashIn,
  cashOut,
  cashAmount,
  bankMobile,
  salesReturn,
  refund,
  creditApplied,
  reservedCounterCash,
  accountableCash,
  closingCash,
  isLoading = false,
}: OutletCheckoutModalProps) {
  const [countedAmount, setCountedAmount] = useState('');
  const [acceptDifference, setAcceptDifference] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showDenominations, setShowDenominations] = useState(false);
  const [billCounts, setBillCounts] = useState<BillCounts>(EMPTY_BILL_COUNTS);
  const [showPaymentSummary, setShowPaymentSummary] = useState(false);

  const counted = countedAmount !== '' ? parseFloat(countedAmount) : 0;
  const difference = counted - closingCash;
  const hasDifference = Math.abs(difference) > 0.01;

  const canConfirm =
    countedAmount !== '' &&
    counted >= 0 &&
    (!hasDifference || acceptDifference);

  const calculateBillTotal = () =>
    BILL_DENOMINATIONS.reduce((sum, bill) => sum + billCounts[bill.key] * bill.value, 0);

  useEffect(() => {
    if (showDenominations) {
      const total = calculateBillTotal();
      setCountedAmount(total > 0 ? total.toFixed(2) : '');
      setAcceptDifference(false);
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDenominations, billCounts]);

  const handleBillCountChange = (key: BillKey, value: string) => {
    const digitsOnly = value.replace(/\D/g, '');
    const numValue = digitsOnly === '' ? 0 : parseInt(digitsOnly, 10);
    setBillCounts({ ...billCounts, [key]: numValue });
  };

  const handleToggleDenominations = () => {
    const next = !showDenominations;
    setShowDenominations(next);
    if (!next) {
      setBillCounts(EMPTY_BILL_COUNTS);
    }
  };

  const handleConfirmRef = useRef<() => void>(() => {});
  useEffect(() => { handleConfirmRef.current = handleConfirm; });
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Enter' && !isSubmitting) handleConfirmRef.current(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isSubmitting]);

  const handleConfirm = async () => {
    if (!canConfirm) {
      if (hasDifference && !acceptDifference) {
        setError('Please accept the payment difference to proceed.');
      } else {
        setError('Please enter a valid counted amount.');
      }
      return;
    }
    setError('');
    setIsSubmitting(true);
    try {
      await onConfirm(counted, acceptDifference, difference);
    } catch (err: any) {
      setError(err?.message || 'Checkout failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg shadow-xl w-full ${showDenominations ? 'max-w-4xl' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto transition-[max-width] duration-200`}>
        {/* Sticky Header */}
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <i className="ri-store-3-line text-xl text-gray-700"></i>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Checkout Summary of ({outletName})</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Outlet Opening Cash:{' '}
                <span className="font-semibold text-gray-700">{outletOpeningCash.toFixed(2)}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer disabled:opacity-50"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <i className="ri-loader-4-line animate-spin text-2xl text-teal-600 mr-2"></i>
              <span className="text-sm text-gray-500">Loading session data...</span>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {/* Left: Session Summary */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <i className="ri-information-line text-blue-600"></i>
                  <p className="text-sm font-medium text-blue-900">Session Summary</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Remaining Cash:</span>
                    <span className="text-sm font-medium text-gray-900">{remainingCash.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Counter Opening Cash:</span>
                    <span className="text-sm font-medium text-gray-900">{totalCounterOpeningCash.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-green-600">+ Sales:</span>
                    <span className="text-sm font-medium text-green-600">{sales.toFixed(2)}</span>
                  </div>
                  {/* Cash In / Cash Out line hidden for now — may be revisited in the future.
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-green-600">+ Cash In:</span>
                    <span className="text-sm font-medium text-green-600">{cashIn.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-red-600">- Cash Out:</span>
                    <span className="text-sm font-medium text-red-600">{cashOut.toFixed(2)}</span>
                  </div>
                  */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Received Cash:</span>
                    <span className="text-sm font-medium text-gray-900">{cashAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <button
                      type="button"
                      onClick={() => setShowPaymentSummary(true)}
                      disabled={!sessionId}
                      className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-70 cursor-pointer"
                    >
                      <span>- Bank/Mobile:</span>
                      <i className="ri-eye-line text-xs"></i>
                    </button>
                    <span className="text-sm font-medium text-red-600">{bankMobile.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-red-600">- Sales Return:</span>
                    <span className="text-sm font-medium text-red-600">{salesReturn.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-red-600">- Refund:</span>
                    <span className="text-sm font-medium text-red-600">{refund.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Credit Applied:</span>
                    <span className="text-sm font-medium text-gray-900">{creditApplied.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Reserved Counter Cash:</span>
                    <span className="text-sm font-medium text-gray-900">{reservedCounterCash.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Accountable Cash:</span>
                    <span className="text-sm font-medium text-gray-900">{accountableCash.toFixed(2)}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-300 flex justify-between items-center">
                    <span className="text-sm font-semibold text-gray-900">Closing Cash:</span>
                    <span className="text-lg font-bold text-gray-900">{closingCash.toFixed(2)}</span>
                  </div>
                </div>

                {showDenominations && countedAmount && counted >= 0 && (
                  <div className={`mt-2 rounded-lg p-2.5 ${hasDifference ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Difference:</span>
                      <span className={`text-lg font-bold ${difference > 0 ? 'text-green-600' : difference < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                        {difference > 0 ? '+' : ''}{difference.toFixed(2)}
                      </span>
                    </div>
                    {hasDifference && (
                      <p className="text-xs text-gray-600 mt-1">
                        {difference > 0 ? 'Cash over expected amount' : 'Cash short of expected amount'}
                      </p>
                    )}
                  </div>
                )}

                {showDenominations && hasDifference && countedAmount && (
                  <label className="mt-2 flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={acceptDifference}
                      onChange={(e) => {
                        setAcceptDifference(e.target.checked);
                        setError('');
                      }}
                      disabled={isSubmitting}
                      className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">
                      Accept Payment Difference and post a profit/loss Journal Entry.
                    </span>
                  </label>
                )}
              </div>

              {/* Right: Count + Difference + Accept */}
              <div className="flex flex-col gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Count Physical Cash <span className="text-red-500">*</span>
                  </label>

                  <button
                    type="button"
                    onClick={handleToggleDenominations}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className={`ri-${showDenominations ? 'checkbox' : 'checkbox-blank'}-line text-lg`}></i>
                    Use cash denomination breakdown
                  </button>

                  {showDenominations && (
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {BILL_DENOMINATIONS.map((bill) => (
                        <div key={bill.key} className="border border-gray-200 rounded-lg p-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold text-gray-700">৳{bill.value.toLocaleString()}</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              placeholder="0"
                              value={billCounts[bill.key] || ''}
                              onChange={(e) => handleBillCountChange(bill.key, e.target.value)}
                              disabled={isSubmitting}
                              className="w-14 px-1.5 py-1 text-xs text-center border border-gray-300 rounded focus:border-teal-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                          </div>
                          <p className="mt-1 text-[11px] text-gray-500 text-right">= ৳{(billCounts[bill.key] * bill.value).toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl">৳</span>
                    <input
                      type={showDenominations ? 'text' : 'number'} onWheel={(e) => e.currentTarget.blur()}
                      value={countedAmount}
                      readOnly={showDenominations}
                      onChange={(e) => {
                        if (showDenominations) return;
                        setCountedAmount(e.target.value);
                        setAcceptDifference(false);
                        setError('');
                      }}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      disabled={isSubmitting || showDenominations}
                      autoFocus={!showDenominations}
                      className={`w-full pl-10 pr-3 py-3 border-2 rounded-lg text-2xl font-bold disabled:cursor-not-allowed ${showDenominations ? 'bg-gray-50 border-gray-200 text-gray-900 disabled:bg-gray-50' : 'border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100'}`}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {showDenominations ? 'Auto-calculated from denomination counts above' : 'Count all bills and coins in the drawer'}
                  </p>
                </div>

                {!showDenominations && countedAmount && counted >= 0 && (
                  <div className={`rounded-lg p-2.5 ${hasDifference ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Difference:</span>
                      <span className={`text-lg font-bold ${difference > 0 ? 'text-green-600' : difference < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                        {difference > 0 ? '+' : ''}{difference.toFixed(2)}
                      </span>
                    </div>
                    {hasDifference && (
                      <p className="text-xs text-gray-600 mt-1">
                        {difference > 0 ? 'Cash over expected amount' : 'Cash short of expected amount'}
                      </p>
                    )}
                  </div>
                )}

                {!showDenominations && hasDifference && countedAmount && (
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={acceptDifference}
                      onChange={(e) => {
                        setAcceptDifference(e.target.checked);
                        setError('');
                      }}
                      disabled={isSubmitting}
                      className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">
                      Accept Payment Difference and post a profit/loss Journal Entry.
                    </span>
                  </label>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-500"></i>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sticky Footer */}
        <div className="flex gap-3 p-4 border-t bg-gray-50 sticky bottom-0">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-gray-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm || isSubmitting || isLoading}
            className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Processing...
              </>
            ) : (
              'Confirm and Checkout'
            )}
          </button>
        </div>
      </div>

      {showPaymentSummary && sessionId && (
        <PaymentSummaryModal
          sessionId={sessionId}
          type="outlet"
          onClose={() => setShowPaymentSummary(false)}
        />
      )}
    </div>
  );
}
